import { useQuery } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'

export type ForecastWeek = {
  weekId: string
  weekStart: string
  weekEnd: string
  opening: number
  income: number
  expense: number
  close: number
  incomeBreakdown: { invoices: number; planned: number }
  expenseBreakdown: { license: number; salaries: number; planned: number }
  flags: { belowThreshold: boolean }
}

export type ForecastReport = {
  asOf: string
  threshold: number
  currency: 'UAH'
  weeks: ForecastWeek[]
}

export type ForecastBreakdownItem = {
  date: string
  source: string
  label: string
  amount: number
}

export type ForecastBreakdown = {
  week: string
  incoming: ForecastBreakdownItem[]
  outgoing: ForecastBreakdownItem[]
}

export type ForecastFilter = {
  organizationId: string
  weeks?: 4 | 8 | 12
  asOf?: string
  enabled?: boolean
}

export type ForecastBreakdownFilter = {
  organizationId: string
  week: string
  asOf?: string
  enabled?: boolean
}

const ROOT = '/forecast'
const KEY = ['forecast'] as const

export function useForecast(filter: ForecastFilter) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'report', filter.organizationId, filter.weeks, filter.asOf],
    enabled: filter.enabled !== false,
    queryFn: () =>
      api<ForecastReport>(
        `${ROOT}${buildQuery({
          organization_id: filter.organizationId,
          weeks: filter.weeks,
          asOf: filter.asOf,
        })}`,
      ),
  })
}

export function useForecastBreakdown(filter: ForecastBreakdownFilter) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'breakdown', filter.organizationId, filter.week, filter.asOf],
    enabled: filter.enabled !== false,
    queryFn: () =>
      api<ForecastBreakdown>(
        `${ROOT}/breakdown${buildQuery({
          organization_id: filter.organizationId,
          week: filter.week,
          asOf: filter.asOf,
        })}`,
      ),
  })
}
